import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import {
  AUTH_SESSION_UPDATED_EVENT,
  autopartsApi,
  readStoredAuthSession,
  type AssistantCapabilitiesResponse,
  type AssistantChatMessage,
  type AssistantFeedbackRating,
  type AssistantOptionSelection,
  type AssistantReplyOption,
  type AuthenticatedUser,
  type MarketplaceRole,
} from "@/api/autoparts";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import {
  Bot,
  CheckCircle,
  Eraser,
  Loader2,
  Send,
  Sparkles,
  X,
  XCircle,
} from "@/components/icons";
import { useDraggableFloating } from "@/hooks/use-draggable-floating";
import { useDocumentModalScrollLock } from "@/hooks/use-document-modal-scroll-lock";
import {
  FLOATING_LAUNCHER_CLASS,
  FLOATING_LAUNCHER_ICON_CLASS,
  FLOATING_LAUNCHER_STACK,
} from "@/components/floating-launcher";

const ASSISTANT_STORAGE_PREFIX = "sparelynk-role-assistant-v3";
const MAX_STORED_MESSAGES = 30;

type AssistantBotProps = {
  pathname: string;
  search: string;
};

type ChatMessage = AssistantChatMessage & {
  id: string;
  createdAt: string;
  isError?: boolean;
  options?: AssistantReplyOption[];
  replyToken?: string;
  provider?: string;
  model?: string;
  confidence?: number;
  feedbackToken?: string;
  feedbackRating?: AssistantFeedbackRating;
  feedbackMessage?: string;
  feedbackError?: boolean;
};

type RoleAssistantConfig = {
  title: string;
  subtitle: string;
  greeting: string;
  placeholder: string;
  disclaimer: string;
};

const ROLE_ASSISTANT_CONFIG: Record<MarketplaceRole, RoleAssistantConfig> = {
  admin: {
    title: "Admin Assistant",
    subtitle: "Here to help with admin tasks",
    greeting:
      "I can guide you through users, product reviews, platform activity, reports, profits, settings, and account security.",
    placeholder: "What can I help you with?",
    disclaimer: "Sensitive admin changes still require system confirmation.",
  },
  seller: {
    title: "Seller Assistant",
    subtitle: "Here to help with seller tasks",
    greeting:
      "I can guide you through listings, inventory, orders, conversations, earnings, profile settings, and account security.",
    placeholder: "What can I help you with?",
    disclaimer: "Sensitive seller changes still require system confirmation.",
  },
  buyer: {
    title: "Buyer Assistant",
    subtitle: "Here to help with buying",
    greeting:
      "I can help you find parts, check compatibility, follow orders, handle payment or refund questions, and message sellers.",
    placeholder: "What can I help you with?",
    disclaimer: "Verify product, order, payment, and fitment details in your account.",
  },
  courier: {
    title: "Courier Assistant",
    subtitle: "Here to help with deliveries",
    greeting:
      "I can guide you through assigned deliveries, tracking, handoffs, delivery messages, earnings, settings, and account security.",
    placeholder: "What can I help you with?",
    disclaimer: "Delivery updates still require confirmation in the delivery workflow.",
  },
  quality_assessor: {
    title: "Quality Assessor Assistant",
    subtitle: "Here to help with assessments",
    greeting:
      "I can guide you through professional credential review status, product authenticity checks, written assessments, and signed quality approvals.",
    placeholder: "What can I help you assess?",
    disclaimer: "Product decisions require your verified credentials and formal signed assessment.",
  },
};

function createMessage(
  role: ChatMessage["role"],
  content: string,
  isError = false,
  options: AssistantReplyOption[] = [],
  replyToken = "",
  metadata?: Pick<
    ChatMessage,
    "provider" | "model" | "confidence" | "feedbackToken"
  >,
): ChatMessage {
  return {
    id: `${role}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    role,
    content,
    createdAt: new Date().toISOString(),
    isError,
    options,
    replyToken,
    provider: metadata?.provider,
    model: metadata?.model,
    confidence: metadata?.confidence,
    feedbackToken: metadata?.feedbackToken,
  };
}

function sanitizeReplyOptions(value: unknown): AssistantReplyOption[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .filter(
      (option): option is AssistantReplyOption =>
        option !== null &&
        typeof option === "object" &&
        typeof (option as AssistantReplyOption).id === "string" &&
        typeof (option as AssistantReplyOption).label === "string" &&
        typeof (option as AssistantReplyOption).message === "string",
    )
    .map((option) => ({
      id: option.id.trim(),
      label: option.label.trim(),
      message: option.message.trim(),
    }))
    .filter(
      (option) => option.id.length > 0 && option.label.length > 0 && option.message.length > 0,
    )
    .slice(0, 3);
}

function getConversationStorageKey(
  user: AuthenticatedUser | null,
): string | null {
  if (!user?.id || !ROLE_ASSISTANT_CONFIG[user.role]) {
    return null;
  }

  return `${ASSISTANT_STORAGE_PREFIX}:${user.id}:${user.role}`;
}

function getGreeting(user: AuthenticatedUser): ChatMessage {
  const firstName = String(user.displayName || user.name || "")
    .trim()
    .split(/\s+/, 1)[0];
  const salutation = firstName ? `Hi ${firstName}!` : "Hi!";
  const config = ROLE_ASSISTANT_CONFIG[user.role];

  return createMessage(
    "assistant",
    `${salutation} Glad you’re here. ${config.greeting} What are you working on?`,
  );
}

function readStoredMessages(user: AuthenticatedUser | null): ChatMessage[] {
  const storageKey = getConversationStorageKey(user);
  if (!user || !storageKey || typeof window === "undefined") {
    return [];
  }

  try {
    const parsed = JSON.parse(window.localStorage.getItem(storageKey) || "[]");
    if (!Array.isArray(parsed)) {
      return [getGreeting(user)];
    }

    const messages = parsed
      .filter(
        (item): item is ChatMessage =>
          item &&
          (item.role === "user" || item.role === "assistant") &&
          typeof item.content === "string" &&
          item.content.trim().length > 0,
      )
      .slice(-MAX_STORED_MESSAGES)
      .map((item) => ({
        id:
          typeof item.id === "string" && item.id
            ? item.id
            : `${item.role}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
        role: item.role,
        content: item.content.trim(),
        createdAt:
          typeof item.createdAt === "string"
            ? item.createdAt
            : new Date().toISOString(),
        isError: Boolean(item.isError),
        options: sanitizeReplyOptions(item.options),
        replyToken:
          typeof item.replyToken === "string" ? item.replyToken.trim() : "",
        provider:
          typeof item.provider === "string" ? item.provider.trim() : undefined,
        model: typeof item.model === "string" ? item.model.trim() : undefined,
        confidence:
          typeof item.confidence === "number" && Number.isFinite(item.confidence)
            ? item.confidence
            : undefined,
        feedbackToken:
          typeof item.feedbackToken === "string"
            ? item.feedbackToken.trim()
            : undefined,
        feedbackRating:
          item.feedbackRating === "correct" || item.feedbackRating === "incorrect"
            ? item.feedbackRating
            : undefined,
        feedbackMessage:
          typeof item.feedbackMessage === "string"
            ? item.feedbackMessage.trim()
            : undefined,
        feedbackError: Boolean(item.feedbackError),
      }));

    return messages.length > 0 ? messages : [getGreeting(user)];
  } catch {
    return [getGreeting(user)];
  }
}

function renderAssistantMessage(
  content: string,
  role: MarketplaceRole,
): ReactNode {
  const linkPattern = /\[([^\]]+)\]\((\/[^)\s]+)\)/g;
  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = linkPattern.exec(content)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(content.slice(lastIndex, match.index));
    }

    const [markdown, label, href] = match;
    const isAllowedPath = href === `/${role}` || href.startsWith(`/${role}/`);

    if (isAllowedPath) {
      nodes.push(
        <a
          key={`${href}-${match.index}`}
          href={href}
          className="font-medium text-primary underline decoration-primary/40 underline-offset-4 hover:decoration-primary"
        >
          {label}
        </a>,
      );
    } else {
      nodes.push(markdown);
    }

    lastIndex = match.index + markdown.length;
  }

  if (lastIndex < content.length) {
    nodes.push(content.slice(lastIndex));
  }

  return nodes.length > 0 ? nodes : content;
}

function getSuggestedPrompts(
  pathname: string,
  role: MarketplaceRole,
): string[] {
  const path = pathname.toLowerCase();

  switch (role) {
    case "admin":
      if (path.includes("/admin/products")) {
        return [
          "How do I review a product listing?",
          "How do I approve or reject a listing?",
          "Which product details should I verify?",
        ];
      }
      if (path.includes("/admin/users")) {
        return [
          "How do I manage a user account?",
          "How do I suspend a user?",
          "Where can I review user status?",
        ];
      }
      return [
        "Where can I view platform activity?",
        "How do I review product listings?",
        "Where can I view platform reports?",
      ];
    case "seller":
      if (path.includes("/seller/inventory")) {
        return [
          "How do I update stock quantity?",
          "How do I mark a product unavailable?",
          "Where can I review inventory?",
        ];
      }
      if (path.includes("/seller/products")) {
        return [
          "How do I add a product listing?",
          "How do I edit product fitment details?",
          "What should I verify before publishing?",
        ];
      }
      if (path.includes("/seller/orders")) {
        return [
          "How do I process a buyer order?",
          "How do I update fulfillment status?",
          "How do I contact the buyer or courier?",
        ];
      }
      return [
        "How do I create a seller listing?",
        "How do I manage a buyer order?",
        "Where can I view seller earnings?",
      ];
    case "buyer":
      if (path.includes("product")) {
        return [
          "How do I check if this part fits my vehicle?",
          "What should I verify before buying this part?",
          "How do I contact the seller?",
        ];
      }
      if (path.includes("/buyer/orders")) {
        return [
          "How do I track my buyer order?",
          "Where can I see payment status?",
          "How do I contact the seller about my order?",
        ];
      }
      return [
        "Help me find a compatible part",
        "How do I track my buyer order?",
        "How do I contact a seller?",
      ];
    case "courier":
      if (path.includes("/courier/deliveries")) {
        return [
          "Where can I see assigned deliveries?",
          "How do I update delivery status?",
          "How do I confirm an order handoff?",
        ];
      }
      return [
        "Where can I see assigned deliveries?",
        "How do I contact the buyer or seller?",
        "Where can I view courier earnings?",
      ];
    case "quality_assessor":
      return [
        "How do I submit my professional credentials?",
        "How do I assess a submitted product?",
        "What must be included in a signed assessment?",
      ];
  }
}

export function AssistantBot({ pathname, search }: AssistantBotProps) {
  const initialUser = readStoredAuthSession()?.user ?? null;
  const [user, setUser] = useState<AuthenticatedUser | null>(initialUser);
  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [feedbackSubmittingID, setFeedbackSubmittingID] = useState("");
  const [feedbackCorrectionID, setFeedbackCorrectionID] = useState("");
  const [feedbackComment, setFeedbackComment] = useState("");
  const [capabilities, setCapabilities] =
    useState<AssistantCapabilitiesResponse | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>(() =>
    readStoredMessages(initialUser),
  );
  const scrollAnchorRef = useRef<HTMLDivElement | null>(null);
  const requestControllerRef = useRef<AbortController | null>(null);
  const [messagesConversationKey, setMessagesConversationKey] = useState(
    getConversationStorageKey(initialUser),
  );
  const assistantLauncher = useDraggableFloating(
    "sparelynk-floating-assistant-position",
    { id: "assistant", stackIndex: FLOATING_LAUNCHER_STACK.assistant },
  );

  const conversationStorageKey = getConversationStorageKey(user);
  const roleConfig = user ? ROLE_ASSISTANT_CONFIG[user.role] : null;
  const suggestions = useMemo(
    () => (user ? getSuggestedPrompts(pathname, user.role) : []),
    [pathname, user],
  );
  const latestAssistantMessageID = useMemo(() => {
    for (let index = messages.length - 1; index >= 0; index -= 1) {
      if (messages[index]?.role === "assistant") {
        return messages[index].id;
      }
    }
    return "";
  }, [messages]);

  useEffect(() => {
    const syncUser = () => {
      setUser(readStoredAuthSession()?.user ?? null);
    };

    window.addEventListener(AUTH_SESSION_UPDATED_EVENT, syncUser);
    window.addEventListener("storage", syncUser);
    return () => {
      window.removeEventListener(AUTH_SESSION_UPDATED_EVENT, syncUser);
      window.removeEventListener("storage", syncUser);
    };
  }, []);

  useEffect(() => {
    if (!user) {
      setCapabilities(null);
      return;
    }

    let active = true;
    void autopartsApi.assistant
      .capabilities()
      .then((value) => {
        if (active) setCapabilities(value);
      })
      .catch(() => {
        if (active) {
          setCapabilities(null);
        }
      });
    return () => {
      active = false;
    };
  }, [user?.id, user?.role]);

  useEffect(() => {
    if (messagesConversationKey === conversationStorageKey) {
      return;
    }

    requestControllerRef.current?.abort();
    requestControllerRef.current = null;
    setIsSending(false);
    setIsOpen(false);
    setDraft("");
    setFeedbackSubmittingID("");
    setFeedbackCorrectionID("");
    setFeedbackComment("");
    setMessages(readStoredMessages(user));
    setMessagesConversationKey(conversationStorageKey);
  }, [conversationStorageKey, messagesConversationKey, user]);

  useEffect(() => {
    if (
      !conversationStorageKey ||
      messagesConversationKey !== conversationStorageKey
    ) {
      return;
    }

    try {
      window.localStorage.setItem(
        conversationStorageKey,
        JSON.stringify(messages.slice(-MAX_STORED_MESSAGES)),
      );
    } catch {
      // The role assistant remains usable when browser storage is unavailable.
    }
  }, [conversationStorageKey, messages, messagesConversationKey]);

  useEffect(() => {
    if (!isOpen) return;
    scrollAnchorRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [isOpen, isSending, messages]);

  useEffect(() => {
    return () => requestControllerRef.current?.abort();
  }, []);

  const clearConversation = useCallback(() => {
    requestControllerRef.current?.abort();
    requestControllerRef.current = null;
    setIsSending(false);
    setMessages(user ? [getGreeting(user)] : []);
    setDraft("");
    setFeedbackSubmittingID("");
    setFeedbackCorrectionID("");
    setFeedbackComment("");
  }, [user]);

  const sendMessage = useCallback(
    async (rawMessage?: string, selection?: AssistantOptionSelection) => {
      const content = String(rawMessage ?? draft).trim();
      if (!user || !content || isSending) return;

      const priorHistory = messages
        .filter((message) => !message.isError)
        .slice(-12)
        .map<AssistantChatMessage>((message) => ({
          role: message.role,
          content: message.content,
        }));
      const userMessage = createMessage("user", content);

      setMessages((current) => [...current, userMessage]);
      setDraft("");
      setIsSending(true);

      const controller = new AbortController();
      requestControllerRef.current?.abort();
      requestControllerRef.current = controller;

      try {
        const response = await autopartsApi.assistant.chat(
          {
            message: content,
            history: priorHistory,
            context: {
              pathname: `${pathname}${search}`,
            },
            selection: selection
              ? {
                  reply_token: selection.replyToken,
                  option_id: selection.optionId,
                }
              : undefined,
          },
          controller.signal,
        );

        const reply = String(response.reply || "").trim();
        const options = sanitizeReplyOptions(response.options);
        const replyToken = String(response.reply_token || "").trim();
        setMessages((current) => [
          ...current,
          createMessage(
            "assistant",
            reply ||
              "I couldn’t form a useful reply just now. Please try asking that another way.",
            false,
            options,
            replyToken,
            {
              provider: String(response.provider || "").trim(),
              model: String(response.model || "").trim(),
              confidence:
                typeof response.confidence === "number"
                  ? response.confidence
                  : undefined,
              feedbackToken: String(response.feedback_token || "").trim(),
            },
          ),
        ]);
      } catch (error) {
        if (controller.signal.aborted) return;

        const message =
          error instanceof Error && error.message.trim()
            ? error.message.trim()
            : "I hit a temporary problem. Please try again in a moment.";
        setMessages((current) => [
          ...current,
          createMessage("assistant", message, true),
        ]);
      } finally {
        if (requestControllerRef.current === controller) {
          requestControllerRef.current = null;
          setIsSending(false);
        }
      }
    }, [draft, isSending, messages, pathname, search, user],
  );

  const submitFeedback = useCallback(
    async (
      message: ChatMessage,
      rating: AssistantFeedbackRating,
      comment = "",
    ) => {
      const feedbackToken = String(message.feedbackToken || "").trim();
      if (!user || !feedbackToken || feedbackSubmittingID) return;

      setFeedbackSubmittingID(message.id);
      setMessages((current) =>
        current.map((item) =>
          item.id === message.id
            ? {
                ...item,
                feedbackMessage: "",
                feedbackError: false,
              }
            : item,
        ),
      );

      try {
        const response = await autopartsApi.assistant.feedback({
          feedback_token: feedbackToken,
          rating,
          comment: comment.trim() || undefined,
        });
        setMessages((current) =>
          current.map((item) =>
            item.id === message.id
              ? {
                  ...item,
                  feedbackRating: rating,
                  feedbackMessage:
                    String(response.message || "").trim() ||
                    "Thanks. Your feedback was recorded.",
                  feedbackError: false,
                  feedbackToken: undefined,
                }
              : item,
          ),
        );
        setFeedbackCorrectionID("");
        setFeedbackComment("");
      } catch (error) {
        const feedbackMessage =
          error instanceof Error && error.message.trim()
            ? error.message.trim()
            : "Feedback could not be saved. Please try again.";
        setMessages((current) =>
          current.map((item) =>
            item.id === message.id
              ? {
                  ...item,
                  feedbackMessage,
                  feedbackError: true,
                }
              : item,
          ),
        );
      } finally {
        setFeedbackSubmittingID("");
      }
    },
    [feedbackSubmittingID, user],
  );

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      void sendMessage();
    }
  };

  const isAssistantDialogOpen =
    isOpen &&
    Boolean(
      user &&
        roleConfig &&
        messagesConversationKey === conversationStorageKey,
    );

  useDocumentModalScrollLock(isAssistantDialogOpen);

  if (
    !user ||
    !roleConfig ||
    messagesConversationKey !== conversationStorageKey
  ) {
    return null;
  }

  return (
    <>
      {isOpen ? (
        <section
          className="fixed bottom-[4.75rem] right-3 z-layer-floating flex h-[min(38rem,calc(100dvh-6rem))] w-[min(24rem,calc(100vw-1.5rem))] flex-col overflow-hidden rounded-2xl border border-border bg-background shadow-2xl sm:bottom-24 sm:right-6"
          role="dialog"
          aria-label={roleConfig.title}
        >
          <header className="flex items-center justify-between border-b border-border bg-muted/40 px-4 py-3">
            <div className="flex min-w-0 items-center gap-3">
              <div className="gradient-accent flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-accent-foreground shadow-sm">
                <Bot className="h-5 w-5" aria-hidden="true" />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h2 className="truncate font-semibold">{roleConfig.title}</h2>
                  <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                    Online
                  </span>
                </div>
                <p className="truncate text-xs text-muted-foreground">
                  {capabilities?.ready && capabilities.provider === "python"
                    ? `Python AI • ${roleConfig.subtitle}`
                    : roleConfig.subtitle}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={clearConversation}
                aria-label={`Clear ${roleConfig.title} conversation`}
              >
                <Eraser className="h-4 w-4" aria-hidden="true" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => setIsOpen(false)}
                aria-label={`Close ${roleConfig.title}`}
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </Button>
            </div>
          </header>

          <ScrollArea className="min-h-0 flex-1">
            <div className="space-y-3 p-4" aria-live="polite">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex ${
                    message.role === "user" ? "justify-end" : "justify-start"
                  }`}
                >
                  <div
                    className={`max-w-[86%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed shadow-sm ${
                      message.role === "user"
                        ? "gradient-accent rounded-br-md text-accent-foreground"
                        : message.isError
                          ? "rounded-bl-md border border-destructive/30 bg-destructive/10 text-foreground"
                          : "rounded-bl-md border border-border bg-muted/55 text-foreground"
                    }`}
                  >
                    <p className="whitespace-pre-wrap break-words">
                      {message.role === "assistant"
                        ? renderAssistantMessage(message.content, user.role)
                        : message.content}
                    </p>
                    {message.role === "assistant" && message.provider ? (
                      <p
                        className="mt-1 text-[10px] text-muted-foreground"
                        title={message.model || undefined}
                      >
                        {message.provider === "python"
                          ? "Python AI"
                          : message.provider === "hybrid"
                            ? "Hybrid AI"
                            : "Built-in AI"}
                      </p>
                    ) : null}
                    {message.role === "assistant" &&
                    !message.isError &&
                    message.options?.length ? (
                      <div className="mt-2 flex flex-col gap-1.5 border-t border-border/70 pt-2">
                        {message.options.map((option) => (
                          <Button
                            key={`${message.id}-${option.id}`}
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-auto justify-start whitespace-normal rounded-lg px-2.5 py-1.5 text-left text-xs"
                            onClick={() =>
                              void sendMessage(
                                option.message,
                                message.replyToken
                                  ? {
                                      replyToken: message.replyToken,
                                      optionId: option.id,
                                    }
                                  : undefined,
                              )
                            }
                            disabled={
                              isSending || message.id !== latestAssistantMessageID
                            }
                          >
                            {option.label}
                          </Button>
                        ))}
                      </div>
                    ) : null}
                    {message.role === "assistant" &&
                    !message.isError &&
                    (message.feedbackToken ||
                      message.feedbackRating ||
                      message.feedbackMessage) ? (
                      <div className="mt-2 border-t border-border/70 pt-2">
                        {message.feedbackRating ? (
                          <div className="flex items-start gap-1.5 text-[11px] text-muted-foreground">
                            {message.feedbackRating === "correct" ? (
                              <CheckCircle
                                className="mt-0.5 h-3.5 w-3.5 shrink-0"
                                aria-hidden="true"
                              />
                            ) : (
                              <XCircle
                                className="mt-0.5 h-3.5 w-3.5 shrink-0"
                                aria-hidden="true"
                              />
                            )}
                            <span>
                              {message.feedbackMessage ||
                                "Thanks. Your feedback was recorded."}
                            </span>
                          </div>
                        ) : (
                          <>
                            <p className="text-[11px] font-medium text-muted-foreground">
                              Was this answer correct?
                            </p>
                            <div className="mt-1.5 flex gap-1.5">
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="h-7 gap-1 px-2 text-[11px]"
                                onClick={() =>
                                  void submitFeedback(message, "correct")
                                }
                                disabled={
                                  feedbackSubmittingID === message.id ||
                                  Boolean(feedbackSubmittingID)
                                }
                              >
                                {feedbackSubmittingID === message.id &&
                                feedbackCorrectionID !== message.id ? (
                                  <Loader2
                                    className="h-3 w-3 animate-spin"
                                    aria-hidden="true"
                                  />
                                ) : (
                                  <CheckCircle
                                    className="h-3 w-3"
                                    aria-hidden="true"
                                  />
                                )}
                                Correct
                              </Button>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="h-7 gap-1 px-2 text-[11px]"
                                onClick={() => {
                                  setFeedbackCorrectionID(message.id);
                                  setFeedbackComment("");
                                }}
                                disabled={
                                  feedbackSubmittingID === message.id ||
                                  Boolean(feedbackSubmittingID)
                                }
                              >
                                <XCircle
                                  className="h-3 w-3"
                                  aria-hidden="true"
                                />
                                Incorrect
                              </Button>
                            </div>
                            {feedbackCorrectionID === message.id ? (
                              <div className="mt-2 space-y-1.5">
                                <Textarea
                                  value={feedbackComment}
                                  onChange={(
                                    event: ChangeEvent<HTMLTextAreaElement>,
                                  ) => setFeedbackComment(event.target.value)}
                                  placeholder="What should be corrected?"
                                  className="min-h-16 resize-none bg-background text-xs"
                                  maxLength={1000}
                                  disabled={feedbackSubmittingID === message.id}
                                  aria-label="Explain what should be corrected"
                                />
                                <div className="flex justify-end gap-1.5">
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 px-2 text-[11px]"
                                    onClick={() => {
                                      setFeedbackCorrectionID("");
                                      setFeedbackComment("");
                                    }}
                                    disabled={feedbackSubmittingID === message.id}
                                  >
                                    Cancel
                                  </Button>
                                  <Button
                                    type="button"
                                    size="sm"
                                    className="h-7 px-2 text-[11px]"
                                    onClick={() =>
                                      void submitFeedback(
                                        message,
                                        "incorrect",
                                        feedbackComment,
                                      )
                                    }
                                    disabled={feedbackSubmittingID === message.id}
                                  >
                                    {feedbackSubmittingID === message.id ? (
                                      <Loader2
                                        className="mr-1 h-3 w-3 animate-spin"
                                        aria-hidden="true"
                                      />
                                    ) : null}
                                    Send feedback
                                  </Button>
                                </div>
                              </div>
                            ) : null}
                            {message.feedbackMessage ? (
                              <p
                                className={`mt-1.5 text-[11px] ${
                                  message.feedbackError
                                    ? "text-destructive"
                                    : "text-muted-foreground"
                                }`}
                              >
                                {message.feedbackMessage}
                              </p>
                            ) : null}
                          </>
                        )}
                      </div>
                    ) : null}
                  </div>
                </div>
              ))}

              {isSending ? (
                <div className="flex justify-start">
                  <div className="flex items-center gap-2 rounded-2xl rounded-bl-md border border-border bg-muted/55 px-3.5 py-2.5 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                    Thinking…
                  </div>
                </div>
              ) : null}
              <div ref={scrollAnchorRef} />
            </div>
          </ScrollArea>

          {messages.length <= 2 ? (
            <div className="border-t border-border px-4 py-3">
              <div className="mb-2 flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
                Try a {user.role} question
              </div>
              <div className="flex flex-wrap gap-2">
                {suggestions.map((suggestion) => (
                  <Button
                    key={suggestion}
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-auto whitespace-normal rounded-full px-3 py-1.5 text-left text-xs"
                    onClick={() => void sendMessage(suggestion)}
                    disabled={isSending}
                  >
                    {suggestion}
                  </Button>
                ))}
              </div>
            </div>
          ) : null}

          <div className="border-t border-border bg-background p-3">
            <div className="flex items-end gap-2 rounded-xl border border-input bg-muted/20 p-2 focus-within:ring-2 focus-within:ring-ring/40">
              <Textarea
                value={draft}
                onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
                  setDraft(event.target.value)
                }
                onKeyDown={handleKeyDown}
                placeholder={roleConfig.placeholder}
                className="max-h-28 min-h-9 resize-none border-0 bg-transparent px-2 py-2 shadow-none focus-visible:ring-0"
                maxLength={2000}
                rows={1}
                disabled={isSending}
                aria-label={`Message the ${roleConfig.title}`}
              />
              <Button
                type="button"
                size="icon"
                className="gradient-accent h-9 w-9 shrink-0 text-accent-foreground"
                onClick={() => void sendMessage()}
                disabled={isSending || !draft.trim()}
                aria-label={`Send message to ${roleConfig.title}`}
              >
                {isSending ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                ) : (
                  <Send className="h-4 w-4" aria-hidden="true" />
                )}
              </Button>
            </div>
            <p className="mt-1.5 text-center text-[10px] text-muted-foreground">
              {roleConfig.disclaimer}
            </p>
          </div>
        </section>
      ) : null}

      <Button
        ref={assistantLauncher.ref}
        type="button"
        data-dragging={assistantLauncher.isDragging}
        className={FLOATING_LAUNCHER_CLASS}
        style={assistantLauncher.style}
        {...assistantLauncher.dragHandlers}
        onClick={() => setIsOpen((current) => !current)}
        aria-label={isOpen ? `Close ${roleConfig.title}` : `Open ${roleConfig.title}`}
        aria-expanded={isOpen}
      >
        {isOpen ? (
          <X className={FLOATING_LAUNCHER_ICON_CLASS} aria-hidden="true" />
        ) : (
          <Bot className={FLOATING_LAUNCHER_ICON_CLASS} aria-hidden="true" />
        )}
      </Button>
    </>
  );
}
